import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

export default function Vendors() {
  const { isOrgAdmin } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/procurement/vendors");
      setVendors(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (vendor) => {
    try {
      await api.patch(`/procurement/vendors/${vendor.id}`, { is_active: !vendor.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <PageHeader title="Vendors" description="Supplier master data used across indents, RFQs and purchase orders." action={isOrgAdmin && <CreateVendorDialog onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && vendors.length === 0 ? (
          <EmptyState title="No vendors yet" testId="vendors-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>GSTIN</TableHead><TableHead>Contact</TableHead><TableHead>Retention / TDS</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {vendors.map((v) => (
                <TableRow key={v.id} data-testid={`vendor-row-${v.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{v.code}</TableCell>
                  <TableCell className="text-foreground">{v.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{v.gstin || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{v.contact_person || v.phone || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
                      {v.default_retention_pct}% / {v.default_tds_pct}%
                      {isOrgAdmin && <VendorTermsDialog vendor={v} onSaved={load} />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={v.is_active} disabled={!isOrgAdmin} onCheckedChange={() => toggleActive(v)} data-testid={`vendor-active-toggle-${v.code}`} />
                      <Badge variant={v.is_active ? "default" : "secondary"}>{v.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateVendorDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", gstin: "", state: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/procurement/vendors", form);
      toast.success(`Vendor ${form.code} created`);
      setForm({ code: "", name: "", gstin: "", state: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-vendor-button"><Plus className="h-4 w-4" /> New Vendor</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Vendor</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-vendor-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required data-testid="vendor-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /><FieldError error={errors.code} /></div>
            <div><Label>Name</Label><Input required data-testid="vendor-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /><FieldError error={errors.name} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>GSTIN</Label><Input data-testid="vendor-gstin-input" value={form.gstin} onChange={set("gstin")} className="mt-1.5" /></div>
            <div><Label>State</Label><Input data-testid="vendor-state-input" value={form.state} onChange={set("state")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact person</Label><Input data-testid="vendor-contact-input" value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="vendor-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Default retention %</Label><Input type="number" step="0.01" min="0" max="100" data-testid="vendor-retention-input" value={form.default_retention_pct} onChange={set("default_retention_pct")} className="mt-1.5" /></div>
            <div><Label>Default TDS %</Label><Input type="number" step="0.01" min="0" max="100" data-testid="vendor-tds-input" value={form.default_tds_pct} onChange={set("default_tds_pct")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-vendor-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VendorTermsDialog({ vendor, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ default_retention_pct: "0", default_tds_pct: "0" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ default_retention_pct: String(vendor.default_retention_pct), default_tds_pct: String(vendor.default_tds_pct) });
  }, [open, vendor]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/procurement/vendors/${vendor.id}`, form);
      toast.success("Vendor retention / TDS updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 px-2" title="Edit retention / TDS" data-testid={`vendor-terms-button-${vendor.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>{vendor.name} — billing terms</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Default retention %</Label><Input type="number" step="0.01" min="0" max="100" value={form.default_retention_pct} onChange={(e) => setForm({ ...form, default_retention_pct: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Default TDS %</Label><Input type="number" step="0.01" min="0" max="100" value={form.default_tds_pct} onChange={(e) => setForm({ ...form, default_tds_pct: e.target.value })} className="mt-1.5" /></div>
          </div>
          <p className="text-[11px] text-muted-foreground">Applies to new service bills. Bills already created keep their own %.</p>
          <DialogFooter>
            <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}